export function V2HarnessSlide({ isActive: _ }: { isActive: boolean }) {
  return (
    <div className="flex flex-col h-full">
      <h2 className="text-[32px] font-light tracking-tight mb-1 font-[family-name:var(--font-heading)]">
        Agents that improve themselves.
      </h2>
      <p className="text-[14px] text-[var(--color-fg-secondary)] leading-relaxed font-light">
        A framework that turns real industry data into benchmarks, and lets agents evolve against them. Continuously. Autonomously.
      </p>

      <div className="flex gap-8 mt-6">
        {/* Left: 3 steps */}
        <div className="flex-1 flex flex-col gap-5">
          {[
            { number: "01", title: "Ingest", description: <>Real case data from partner firms. Documents, workflows, decisions, outcomes. <strong className="font-medium text-[var(--color-fg)]">Irreversibly anonymised</strong> under standard DPA.</> },
            { number: "02", title: "Benchmark", description: <>Build accuracy baselines per task: form-filling precision, document conflict detection, compliance check coverage. Agents scored against <strong className="font-medium text-[var(--color-fg)]">professional-grade standards</strong>.</> },
            { number: "03", title: "Evolve", description: <>Agents identify their own weak points, target improvement areas, and re-test against benchmarks. <strong className="font-medium text-[var(--color-fg)]">Autonomous improvement without manual retraining</strong>.</> },
          ].map((step, i) => (
            <div key={step.number} className="flex gap-3">
              <div className="flex flex-col items-center shrink-0 pt-0.5">
                <div className="w-[20px] h-[20px] rounded-full border border-[var(--color-accent)] flex items-center justify-center">
                  <span className="text-[8px] font-[family-name:var(--font-mono)] text-[var(--color-accent)]">{step.number}</span>
                </div>
                {i < 2 && <div className="w-px flex-1 bg-[var(--color-border)] mt-1" />}
              </div>
              <div>
                <h3 className="text-[14px] font-medium mb-1">{step.title}</h3>
                <p className="text-[12px] text-[var(--color-fg-secondary)] leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Right: Flywheel diagram */}
        <div className="w-[320px] shrink-0 flex items-center justify-center">
          <svg viewBox="0 0 280 280" className="w-full h-auto" style={{ maxHeight: 260 }}>
            {(() => {
              const cx = 140, cy = 140, r = 88;
              const toRad = (d: number) => (d * Math.PI) / 180;
              const ptX = (a: number, rad = r) => cx + rad * Math.cos(toRad(a));
              const ptY = (a: number, rad = r) => cy + rad * Math.sin(toRad(a));

              // 5 nodes every 72°, starting top (270°)
              const nodes = [
                { a: 270, label: "INGEST", anchor: "middle" as const, dy: -8 },
                { a: 342, label: "BENCHMARK", anchor: "start" as const, dy: 2 },
                { a: 54,  label: "EVOLVE", anchor: "start" as const, dy: 5 },
                { a: 126, label: "BETTER\nPRODUCT", anchor: "end" as const, dy: 3 },
                { a: 198, label: "MORE\nPARTNERS", anchor: "end" as const, dy: -2 },
              ];

              // SVG arc from angle s to angle e (clockwise, small arc per segment)
              const arc = (s: number, e: number) => {
                const x1 = ptX(s), y1 = ptY(s), x2 = ptX(e), y2 = ptY(e);
                const sweep = 1; // clockwise
                const large = ((e - s + 360) % 360) > 180 ? 1 : 0;
                return `M${x1},${y1} A${r},${r} 0 ${large} ${sweep} ${x2},${y2}`;
              };

              // Arc segments: go through all 5 nodes, stop 15° before INGEST
              const segments = [
                [270, 342], [342, 414], [54, 126], [126, 198], [198, 255],
              ];

              // Arrow at 255° pointing toward 270°
              const aa = toRad(255), tangent = aa + Math.PI / 2;
              const ax = ptX(255), ay = ptY(255);
              const sz = 7;

              return (
                <>
                  {/* Faint background circle */}
                  <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--color-border)" strokeWidth="0.5" opacity="0.15" />

                  {/* Arc segments — guaranteed to pass through nodes */}
                  {segments.map(([s, e], i) => (
                    <path key={i} d={arc(s!, e!)} fill="none" stroke="var(--color-accent)" strokeWidth="1.5" />
                  ))}

                  {/* Arrowhead */}
                  <polygon
                    points={`${ax + sz * Math.cos(tangent)},${ay + sz * Math.sin(tangent)} ${ax + sz * Math.cos(tangent + 2.3)},${ay + sz * Math.sin(tangent + 2.3)} ${ax + sz * Math.cos(tangent - 2.3)},${ay + sz * Math.sin(tangent - 2.3)}`}
                    fill="var(--color-accent)"
                  />

                  {/* Center label */}
                  <text x={cx} y={cy - 3} textAnchor="middle" fill="var(--color-fg)" fontSize="12" fontFamily="var(--font-heading)" fontWeight="300" opacity="0.35">Data</text>
                  <text x={cx} y={cy + 11} textAnchor="middle" fill="var(--color-fg)" fontSize="12" fontFamily="var(--font-heading)" fontWeight="300" opacity="0.35">Flywheel</text>

                  {/* Nodes + labels */}
                  {nodes.map((n, i) => {
                    const x = ptX(n.a), y = ptY(n.a);
                    const lr = r + 16;
                    const lx = ptX(n.a, lr), ly = ptY(n.a, lr);
                    const lines = n.label.split("\n");
                    return (
                      <g key={i}>
                        <circle cx={x} cy={y} r="4.5" fill="var(--color-accent)" />
                        {lines.map((line, li) => (
                          <text
                            key={li}
                            x={lx}
                            y={ly + n.dy + li * 11}
                            textAnchor={n.anchor}
                            fill="var(--color-fg-secondary)"
                            fontSize="10"
                            fontFamily="var(--font-mono)"
                            letterSpacing="0.06em"
                          >
                            {line}
                          </text>
                        ))}
                      </g>
                    );
                  })}
                </>
              );
            })()}
          </svg>
        </div>
      </div>

      <div className="text-left mt-auto mb-8 pb-4 border-b border-[var(--color-border)]">
        <p className="text-[12px] text-[var(--color-fg-secondary)] font-light">
          This is why more partners means better agents, not just more revenue. <strong className="font-medium text-[var(--color-fg)]">Competitors without this data start from zero.</strong>
        </p>
      </div>
    </div>
  );
}
